import { round } from "mathjs";
import { Offer, OfferItemGroup, Project, Supplier } from "src/app/domains/internal";

import { Tag } from "../features/eva-company/domains/eva-company-tag";
import { Intervention } from "../features/interventions/domains/intervention";
import { InvoicePdfConfig } from "../features/offer-v2/domains/InvoicePdfConfig";
import { PurchaseOrder } from "../features/purchase-order/domains/purchase-order";
import { getTodayUtc } from "../shared/helpers/date.helpers";
import {
  IBAN_CODE_LENGTHS,
  isValidIbanChecksum,
  roundAmount,
  roundToNearestCents,
} from "../shared/helpers/number.helper";
import {
  classifyPaymentReference,
  getPaymentReferenceError,
  isQrIban,
  ReferenceKind,
} from "../shared/helpers/payment-reference.helper";
import { AdditionalAddress } from "./additional-address";
import { ChiftIntegrationEntityLink } from "./chift-integration-entity-link";
import { CreditNote } from "./credit-note";
import { CurrencyDetails } from "./currency_details";
import { InvoiceDiscount, InvoiceDiscountType } from "./invoice-discount";
import { OfferItemArticle, OfferUnitType, roundFixed, toNumber } from "./offer-item";
import {
  initPaymentConditions,
  PaymentCondition,
  paymentDeadlineFrom,
  resolvePaymentConditions,
} from "./payment-condition";
import { ProjectRepartition } from "./project-repartition";

export enum InvoiceBillingType {
  DEPOSIT = "DEPOSIT",
  TOTAL = "TOTAL",
  WARRANTY = "WARRANTY",
}
export enum InvoiceEntry {
  FREE_BILLING = "FREE_BILLING",
  ACCORDING_TO_OFFER = "ACCORDING_TO_OFFER",
}

export enum InvoiceType {
  RECEIVED = "RECEIVED",
  ISSUED = "ISSUED",
}

export enum FreeBillingItemType {
  MATERIAL = "MATERIAL",
  WORKFORCE = "WORKFORCE",
  OTHER = "OTHER",
  TEXT = "TEXT",
  PACK = "PACK",
}

export enum InvoiceStatus {
  PAID = "PAID",
  ACCOUNTED = "ACCOUNTED",
  CANCELLED = "CANCELLED",
  ON_GOING = "ON_GOING",
  DRAFT = "DRAFT",
  VALIDATED = "VALIDATED",
  SENT = "SENT",
}

export enum InvoiceStatusLabel {
  DRAFT = "Brouillon",
  PAID = "Payée",
  ACCOUNTED = "Mise en comptabilité",
  CANCELLED = "Abandonnée",
  ON_GOING = "En attente de paiement",
  VALIDATED = "Acceptée",
  SENT = "Envoyée",
}

export enum InvoiceIssuedSubtype {
  OFFER = "OFFER",
  CONTRACT = "CONTRACT",
  INTERVENTION = "INTERVENTION",
  FREE = "FREE",
}

/** Position de MO pure dont la MO est coupée, sur objet brut ou instance. */
export function isFreeBillingWorkforceLineDisabled(item: {
  type: FreeBillingItemType | string;
  is_workforce_disabled?: boolean;
}): boolean {
  return item?.type === FreeBillingItemType.WORKFORCE && item?.is_workforce_disabled === true;
}

/** Ligne comptée dans les totaux, sur objet brut ou instance. */
export function freeBillingItemCountsInTotals(item: {
  type: FreeBillingItemType | string;
  is_workforce_disabled?: boolean;
}): boolean {
  return item?.type !== FreeBillingItemType.TEXT && !isFreeBillingWorkforceLineDisabled(item);
}

/** Rattache le client à une nouvelle facture : 1re adresse de facturation et échéance selon ses conditions. */
export function prefillInvoiceCustomer(
  invoice: Invoice,
  customer: {
    customer_id: string;
    addresses?: { address_id: string; is_for_invoices: boolean }[];
    payment_conditions?: PaymentCondition[];
  },
  companyConditions: PaymentCondition[] | null | undefined,
): void {
  invoice.customer_id = customer.customer_id;
  invoice.invoice_address_id = customer.addresses?.find((address) => address.is_for_invoices === true)?.address_id;
  invoice.payment_deadline = paymentDeadlineFrom(
    invoice.invoice_date,
    resolvePaymentConditions(customer.payment_conditions, companyConditions),
  );
}

export class FreeBillingItem {
  item_id: string;
  name: string;
  quantity: number;
  unity: string;
  unit_price: number;
  price: number;
  type: FreeBillingItemType;
  discount_percent: number;
  discount_value: number;
  discount_type: InvoiceDiscountType;
  isPageBreakBefore: boolean;
  isSelected: boolean;
  // Composition d'un pack inséré depuis le catalogue (sous-articles avec prix de revient/
  // marge). Reste interne : le PDF client n'affiche que la ligne du pack, pas sa composition.
  articles: OfferItemArticle[];
  is_workforce_disabled: boolean = false;
  // OfferItem source : conserve les champs non gérés par l'écran de facturation
  // (fournisseur, article, TVA, type, description, etc.) pour ne pas les perdre
  // lors du round-trip OfferItem -> FreeBillingItem -> OfferItem au moment de facturer.
  source_offer_item?: any;

  constructor() {
    this.item_id = generateObjectId();
    this.quantity = 1;
    this.unit_price = 0;
    this.price = 0;
    this.unity = "pce";
    this.type = FreeBillingItemType.MATERIAL;
    this.discount_percent = 0;
    this.discount_value = 0;
    this.discount_type = InvoiceDiscountType.PERCENT;
    this.name = "";
    this.articles = [];
  }

  public static createInstance(jsonData: any): FreeBillingItem {
    const f = Object.assign(new FreeBillingItem(), jsonData);
    f.articles = OfferItemArticle.createInstances(jsonData?.articles);
    f.is_workforce_disabled = jsonData?.is_workforce_disabled === true;

    return f;
  }

  public static createInstances(jsonData: any[]): FreeBillingItem[] {
    return jsonData.map((f) => FreeBillingItem.createInstance(f));
  }

  /** Position de MO pure dont la MO est coupée : exclue des totaux. */
  get isWorkforceLineDisabled(): boolean {
    return isFreeBillingWorkforceLineDisabled(this);
  }

  /** État affiché du toggle, packs inclus. */
  get isWorkforceDisabled(): boolean {
    if (this.type === FreeBillingItemType.PACK) {
      const workforceArticles = (this.articles ?? []).filter((a) => a.article_type === OfferUnitType.WORKFORCE);
      return workforceArticles.length > 0 && workforceArticles.every((a) => a.is_workforce_disabled);
    }
    return this.isWorkforceLineDisabled;
  }

  /** Le toggle n'a de sens que sur un pack porteur de MO ou une position de MO pure. */
  get hasWorkforceToToggle(): boolean {
    if (this.type === FreeBillingItemType.PACK) {
      return (this.articles ?? []).some((a) => a.article_type === OfferUnitType.WORKFORCE);
    }
    return this.type === FreeBillingItemType.WORKFORCE;
  }

  get countsInTotals(): boolean {
    return freeBillingItemCountsInTotals(this);
  }

  /**
   * Non destructif : un pack voit la part de MO de ses sous-lignes retranchée de unit_price
   * (puis rajoutée à l'identique à la réactivation) ; une position de MO pure ne change que
   * son drapeau. `price` reste à recalculer par l'appelant.
   */
  setWorkforceDisabled(disabled: boolean) {
    if (this.type === FreeBillingItemType.PACK) {
      this.shiftPackWorkforceShare(disabled);
      return;
    }
    if (this.type === FreeBillingItemType.WORKFORCE) {
      this.is_workforce_disabled = disabled;
    }
  }

  private shiftPackWorkforceShare(disabled: boolean) {
    if (!Array.isArray(this.articles)) this.articles = [];

    let sellingShare = 0;
    for (const article of this.articles) {
      if (article.article_type !== OfferUnitType.WORKFORCE) continue;
      if (!!article.is_workforce_disabled === disabled) continue;
      article.is_workforce_disabled = disabled;
      sellingShare += roundFixed(toNumber(article.selling_price), 2);
    }
    if (sellingShare === 0) return;

    const sign = disabled ? -1 : 1;
    this.unit_price = roundFixed(toNumber(this.unit_price) + sign * sellingShare, 6);
  }

  replaceIds(): void {
    this.item_id = generateObjectId();
    // Nouvelle ligne (copier-coller / duplication) : ne pas hériter de la source
    // d'une autre ligne (sinon fournisseur/article/TVA recopiés par erreur).
    this.source_offer_item = undefined;
  }
}
export class FreeBillingItemGroup {
  item_group_id: string;
  name: string;
  group_type: string;
  isPageBreakBefore: boolean;
  items: FreeBillingItem[] = [];

  constructor() {
    this.item_group_id = generateObjectId();
  }

  public static createInstance(jsonData: any): FreeBillingItemGroup {
    const f = Object.assign(new FreeBillingItemGroup(), jsonData);

    f.items = jsonData.items ? FreeBillingItem.createInstances(jsonData.items) : [];

    return f;
  }

  public static createInstances(jsonData: any[]): FreeBillingItemGroup[] {
    return jsonData.map((f) => FreeBillingItemGroup.createInstance(f));
  }

  setWorkforceDisabled(disabled: boolean) {
    for (const item of this.items ?? []) {
      item.setWorkforceDisabled(disabled);
    }
  }

  replaceIds(): void {
    this.item_group_id = generateObjectId();
    this.items.forEach((item) => item.replaceIds());
  }
}

export class InvoicePayment {
  invoice_payment_id: string;
  gross_amount: number;
  vat_rate: number;
  net_pt_amount: number;
  is_full_payment = true;
  proof_asset_id: string;
  proof_asset_name: string;
  paid_by: string;
  paid_on: Date;
  comment: string;

  constructor() {
    this.invoice_payment_id = generateObjectId();
  }

  public static createInstance(jsonData: InvoicePayment): InvoicePayment {
    const i = Object.assign(new InvoicePayment(), jsonData);

    i.paid_on = jsonData.paid_on ? new Date(jsonData.paid_on) : null;

    return i;
  }
  public static createInstances(objs: any[]): InvoicePayment[] {
    if (!Array.isArray(objs)) {
      objs = [objs];
    }
    return objs.map(InvoicePayment.createInstance);
  }
}

export class Invoice {
  invoice_id: string;
  invoice_corporate_id: string;
  bexio_invoice_id: number;
  eva_company_id: string;
  pdf_config: InvoicePdfConfig;
  accounting_category: string = "";
  free_billing_items_groups: FreeBillingItemGroup[] = [];
  projects_repartition: ProjectRepartition[] = [];
  chift_links: ChiftIntegrationEntityLink[] = [];
  display_contact_name: boolean;
  invoice_type: string;
  invoice_asset_id: string;
  invoice_asset_name: string;
  invoice_address_id: string;
  additional_address: AdditionalAddress;
  invoice_ref: string;
  currency_details: CurrencyDetails;

  // Renvoyés uniquement par POST /invoices/offer/:offer_id (create_offer_invoice) : la création de
  // facture bump updated_on/updated_by de l'offre parente côté serveur, on les remonte pour que
  // l'appelant resynchronise son état et évite un faux conflit OFFER_NOT_UP_TO_DATE à la prochaine
  // sauvegarde (même pattern que CallToTender.offer_updated_on/offer_updated_by).
  offer_updated_on?: Date;
  offer_updated_by?: string;

  invoice_discounts: InvoiceDiscount[];
  public setInvoiceDiscounts(invoiceDiscounts: InvoiceDiscount[]) {
    this.invoice_discounts = invoiceDiscounts;
  }

  purchase_discount_percent: number = 0;
  public setDiscountRate(discountRate: number) {
    this.purchase_discount_percent = discountRate;
    this.purchase_discount_amount = roundAmount(this.gross_total_price * (this.purchase_discount_percent / 100));
    this.net_total_price = this.computeNetTotalPrice();
    this.vat_amount = this.computeVat();
    this.total_pt = this.computeNetVatTotalPrice();
  }

  purchase_discount_amount: number = 0;

  credit_note_gross_price: number = 0.0;
  public setCreditNotePrice(grossPrice: number) {
    this.credit_note_gross_price = grossPrice;
    this.net_total_price = this.computeNetTotalPrice();
    this.vat_amount = this.computeVat();
    this.total_pt = this.computeNetVatTotalPrice();
  }

  gross_total_price: number = 0;
  public setGrossTotalPrice(grossTotalPrice: number) {
    this.gross_total_price = grossTotalPrice;
    this.setDiscountRate(this.purchase_discount_percent);
    this.net_total_price = this.computeNetTotalPrice();
    this.vat_amount = this.computeVat();
    this.total_pt = this.computeNetVatTotalPrice();
  }

  vat_rate: number = 0.0;
  public setVatRate(vatRate: number) {
    this.vat_rate = vatRate;
    // La mention d'exonération de TVA ne vaut qu'à 0 %.
    if (vatRate !== 0) {
      this.vat_exemption_mention = false;
    }
    this.net_total_price = this.computeNetTotalPrice();
    this.vat_amount = this.computeVat();
    this.total_pt = this.computeNetVatTotalPrice();
  }

  round_total_pt = 0.05;
  public setRoundTotalPt(round_total_pt: number) {
    this.round_total_pt = round_total_pt;
    this.recalculatePrices();
  }

  currency = "CHF";
  vat_exemption_mention = false;
  public setCurrency(currency: string) {
    if (["CHF", "EUR"].includes(currency)) {
      this.currency = currency;
    } else {
      this.currency = "CHF";
    }
  }

  vat_amount: number = null;
  public setVatAmount(vatAmount: number) {
    this.vat_amount = vatAmount;
  }

  total_pt: number = null;
  public setTotalPt(totalPt: number) {
    this.total_pt = totalPt;
  }
  net_total_price: number = 0;
  invoice_about: string;

  invoice_date: Date;
  start_date_contract_work: Date;
  end_date_contract_work: Date;
  payment_deadline: Date;

  tags_ids: string[] = [];
  tags: Tag[] = [];

  issued_show_details_offer: boolean = false;

  issued_show_summary_offer: boolean = true;
  issued_show_recap: boolean = true;
  invoice_entry: string;

  is_qr_invoice: boolean = false;
  qr_code: string;

  status: InvoiceStatus;

  payment_proof_asset_id: string;
  payment_proof_asset_name: string;

  payment_iban: string;
  payment_ref: string;
  payment_add_info: string;
  payments: InvoicePayment[] = [];

  // logs
  created_by: string;
  created_on: Date;
  validated_by: string;
  validated_on: Date;
  sent_by: string;
  sent_on: Date;
  send_type: string;
  paid_by: string;
  paid_on: Date;
  cancelled_by: string;
  cancelled_on: Date;
  accounted_by: string;
  accounted_on: Date;
  scheduled_by: string;
  scheduled_on: Date;

  comment: string;

  // received invoice
  purchase_order_id: string;

  // emitted invoice
  offer_id: string;
  intervention_ids: string[] = [];
  intervention_quotation_more: OfferItemGroup;
  intervention_quotations_to_include: string[] = [];
  contract_id: string;
  contract_quotation: OfferItemGroup;
  paid_gross_total_price: number;
  paid_vat_amount: number;
  paid_total_pt: number;
  contacts_ids: string[] = [];

  // external data to load
  purchase_order: PurchaseOrder;
  interventions: Intervention[];
  customer_id: string;
  external_contract_id: string;
  customer_name: string;
  sent_by_employee_name: string;

  project: any;
  supplier: Supplier;
  offer: any;

  project_id = "";
  site_id = "";
  supplier_id = "";
  project_corporate_id = "";
  project_name = "";
  offer_corporate_id = "";
  contract_corporate_id = "";
  interventions_nb = 0;
  execution_date: Date;

  get cd_fc_rate(): number {
    return this.currency_details.fc_rate;
  }
  get cd_gross_total_price_pc(): number {
    return this.currency_details.gross_total_price_pc;
  }
  get cd_vat_amount_pc(): number {
    return this.currency_details.vat_amount_pc;
  }
  get cd_total_pt_pc(): number {
    return this.currency_details.total_pt_pc;
  }
  get cd_total_bt_pc(): number {
    return this.currency_details.total_bt_pc;
  }
  get cd_paid_gross_total_price_pc(): number {
    return this.currency_details.paid_gross_total_price_pc;
  }
  get cd_paid_vat_amount_pc(): number {
    return this.currency_details.paid_vat_amount_pc;
  }
  get cd_paid_total_pt_pc(): number {
    return this.currency_details.paid_total_pt_pc;
  }

  get late(): boolean {
    let today = new Date();
    if ([InvoiceStatus.ON_GOING, InvoiceStatus.SENT, InvoiceStatus.VALIDATED].includes(this.status)) {
      if (today > this.payment_deadline) {
        return true;
      }
    }
    return false;
  }

  get shouldShowDiscountColumns() {
    return this.issued_subtype !== InvoiceIssuedSubtype.OFFER && this.hasDiscountedItems;
  }

  get hasDiscountedItems() {
    return this.free_billing_items_groups.some((group) =>
      group.items.some((item) => item.discount_percent > 0 && item.type !== FreeBillingItemType.TEXT),
    );
  }

  get issued_subtype() {
    if (this.offer_id) {
      return InvoiceIssuedSubtype.OFFER;
    } else if (this.intervention_ids && this.intervention_ids.length > 0) {
      return InvoiceIssuedSubtype.INTERVENTION;
    } else if (this.contract_id) {
      return InvoiceIssuedSubtype.CONTRACT;
    } else {
      return InvoiceIssuedSubtype.FREE;
    }
  }

  get interventions_count(): number {
    return this.intervention_ids?.length ?? 0;
  }

  get purchase_order_corporate_id(): string {
    return this.purchase_order ? this.purchase_order.purchase_order_corporate_id : "";
  }

  get supplier_name(): string {
    return this.supplier ? this.supplier.name : "";
  }

  get purchase_order_fulfilment(): number {
    return this.purchase_order ? this.purchase_order.percentBilled : 0;
  }

  get purchase_order_delivered(): number {
    return this.purchase_order ? this.purchase_order.percentDelivered : 0;
  }

  get excelExport() {
    return {
      "Bon de commande": this.purchase_order?.purchase_order_corporate_id,
      Facture: this.invoice_corporate_id,
      "Chantier numéro": this.project?.project_corporate_id,
      "Chantier nom": this.project?.name,
      Founisseur: this.supplier?.name,
      "Rabais fournisseur": this.getSupplierDiscount(),
      Devise: this.currency,
      "Brut HT": this.gross_total_price,
      "Total HT": this.getNetTotalPrice(),
      "Taux de TVA": this.vat_rate,
      "Montant TVA": this.vat_amount,
      "Total TTC": this.getNetVatTotalPrice(),
      "Dû NET HT": this.leftToPayIncludingDiscounts,
      "Dû TVA": this.leftToPayTtc - this.leftToPayIncludingDiscounts,
      "Dû TTC": this.leftToPayTtc,
      "Payé HT": this.paid_gross_total_price,
      "Payé TVA": this.paid_vat_amount,
      "Payé TTC": this.paid_total_pt,
      "Date facture": this.invoice_date,
      "Date d'échéance": this.payment_deadline,
      "Retard de paiement": this.paymentDelay + " jours",
      Payée: this.paid ? "OUI" : this.hasPayments ? "PARTIEL" : "NON",
      "Date de paiement": this.paid_on,
      "% Remplissage bon": this.purchase_order_fulfilment,
      Statut: this.status,
    };
  }

  get excelIssuedExport() {
    return {
      Facture: this.invoice_corporate_id,
      Chantier: this.project_id ? `${this.project_corporate_id} - ${this.project_name}` : "",
      Client: this.customer_name,
      Offre: this.offer_corporate_id,
      Devise: this.currency,
      "Total HT": this.getNetTotalPrice(),
      "Taux de TVA": this.vat_rate,
      "Montant TVA": this.vat_amount,
      "Total TTC": this.getNetVatTotalPrice(),
      "Dû NET HT": this.leftToPayIncludingDiscounts,
      "Dû TVA": this.leftToPayTtc - this.leftToPayIncludingDiscounts,
      "Dû TTC": this.leftToPayTtc,
      "Payé HT": this.paid_gross_total_price,
      "Payé TVA": this.paid_vat_amount,
      "Payé TTC": this.paid_total_pt,
      "Date de la facture": this.invoice_date,
      "Date de validation": this.validated_on,
      "Envoyée au client": this.sent ? "OUI" : "NON",
      "Date de d'envoi": this.sent_on,
      "Echéance de paiement": this.payment_deadline,
      "Retard de paiement": this.paymentDelay > 0 ? this.paymentDelay : 0,
      "Payée par le client": this.paid ? "OUI" : this.hasPayments ? "PARTIEL" : "NON",
      "Date de paiement": this.paid_on,
      Status: this.status,
    };
  }

  get validated() {
    return this.validated_on !== null;
  }
  set validated(val) {
    //ignored, to make binding checkbox successful
  }

  get sent() {
    return this.sent_on !== null;
  }
  set sent(val) {
    //ignored, to make binding checkbox successful
  }
  get paid() {
    return this.paid_on !== null;
  }
  set paid(val) {
    //ignored, to make binding checkbox successful
  }

  get paymentDelay(): number {
    let dateCompare = this.paid_on ? new Date(this.paid_on) : new Date();
    if (this.payment_deadline) {
      return Math.round((dateCompare.getTime() - this.payment_deadline.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      return 0;
    }
  }

  get netTotalPrice(): number {
    return this.getNetTotalPrice();
  }

  get netTotalPricePc(): number {
    return this.getNetTotalPricePc();
  }

  get netVatTotalPrice(): number {
    return this.getNetVatTotalPrice();
  }

  get netVatTotalPricePc(): number {
    return this.getNetVatTotalPricePc();
  }

  get netVatTotalPriceStr(): string {
    return this.netVatTotalPrice.toFixed(2);
  }

  get vat(): number {
    return this.getVat();
  }

  get supplierDiscount(): number {
    return this.purchase_discount_percent || 0;
  }

  get accounted() {
    return !!this.accounted_on;
  }
  set accounted(val) {
    if (val === false) {
      this.accounted_on = null;
      this.accounted_by = null;
    } else {
      if (this.accounted_on == null) this.accounted_on = new Date();
    }
  }

  get scheduled() {
    return !!this.scheduled_on;
  }
  set scheduled(val) {
    if (val === false) {
      this.scheduled_on = null;
      this.scheduled_by = null;
    } else {
      if (this.scheduled_on == null) this.scheduled_on = new Date();
    }
  }

  constructor() {
    this.status = InvoiceStatus.DRAFT;
    this.invoice_date = getTodayUtc();
    this.start_date_contract_work = getTodayUtc();
    this.end_date_contract_work = getTodayUtc();
    this.payment_deadline = paymentDeadlineFrom(this.invoice_date, initPaymentConditions);
    this.pdf_config = new InvoicePdfConfig();
    this.additional_address = new AdditionalAddress();
    this.currency_details = new CurrencyDetails();
    this.invoice_entry = "ACCORDING_TO_OFFER";
    this.display_contact_name = true;
    this.invoice_discounts = [];
  }

  public static createInstance(jsonData: Invoice): Invoice {
    const i = Object.assign(new Invoice(), jsonData);

    i.payment_deadline = jsonData.payment_deadline ? new Date(jsonData.payment_deadline) : null;

    i.created_on = jsonData.created_on ? new Date(jsonData.created_on) : null;
    i.validated_on = jsonData.validated_on ? new Date(jsonData.validated_on) : null;
    i.sent_on = jsonData.sent_on ? new Date(jsonData.sent_on) : null;
    i.paid_on = jsonData.paid_on ? new Date(jsonData.paid_on) : null;
    i.cancelled_on = jsonData.cancelled_on ? new Date(jsonData.cancelled_on) : null;
    i.invoice_date = jsonData.invoice_date ? new Date(jsonData.invoice_date) : null;
    i.start_date_contract_work = jsonData.start_date_contract_work ? new Date(jsonData.start_date_contract_work) : null;
    i.end_date_contract_work = jsonData.end_date_contract_work ? new Date(jsonData.end_date_contract_work) : null;
    i.accounted_on = jsonData.accounted_on ? new Date(jsonData.accounted_on) : null;
    i.scheduled_on = jsonData.scheduled_on ? new Date(jsonData.scheduled_on) : null;
    i.offer_updated_on = jsonData.offer_updated_on ? new Date(jsonData.offer_updated_on) : null;

    i.offer = jsonData.offer ? Offer.createInstance(jsonData.offer) : null;
    i.pdf_config = jsonData.pdf_config ? InvoicePdfConfig.createInstance(jsonData.pdf_config) : null;
    i.additional_address = jsonData.additional_address
      ? AdditionalAddress.createInstance(jsonData.additional_address)
      : null;
    i.currency_details = jsonData.currency_details
      ? CurrencyDetails.createInstance(jsonData.currency_details)
      : new CurrencyDetails();
    if (jsonData.project) {
      i.project = Project.createInstance(jsonData.project);
      i.project_id = i.project_id ? i.project_id : i.project.project_id;
      i.project_corporate_id = i.project_corporate_id ? i.project_corporate_id : i.project.project_corporate_id;
      i.project_name = i.project_name ? i.project_name : i.project.name;
    }
    i.supplier = jsonData.supplier ? Supplier.createInstance(jsonData.supplier) : null;
    i.invoice_entry = jsonData.invoice_entry;
    i.purchase_order = jsonData.purchase_order ? PurchaseOrder.createInstance(jsonData.purchase_order) : null;
    i.intervention_quotation_more = jsonData.intervention_quotation_more
      ? OfferItemGroup.createInstance(jsonData.intervention_quotation_more)
      : null;
    i.interventions = jsonData.interventions ? Intervention.createInstances(jsonData.interventions) : [];
    i.free_billing_items_groups =
      jsonData.free_billing_items_groups && jsonData.free_billing_items_groups.length > 0
        ? FreeBillingItemGroup.createInstances(jsonData.free_billing_items_groups)
        : [new FreeBillingItemGroup()];

    i.projects_repartition =
      jsonData.projects_repartition && jsonData.projects_repartition.length > 0
        ? ProjectRepartition.createInstances(jsonData.projects_repartition)
        : [];

    i.payments =
      jsonData.payments && jsonData.payments.length > 0 ? InvoicePayment.createInstances(jsonData.payments) : [];
    i.chift_links =
      jsonData.chift_links && jsonData.chift_links.length > 0
        ? ChiftIntegrationEntityLink.createInstances(jsonData.chift_links)
        : [];

    i.contract_quotation = jsonData.contract_quotation
      ? OfferItemGroup.createInstance(jsonData.contract_quotation)
      : null;
    i.tags = jsonData.tags ? Tag.createInstances(jsonData.tags) : [];
    i.tags_ids = Array.isArray(jsonData.tags_ids) ? jsonData.tags_ids : [];

    i.invoice_discounts = [];

    let newPrice = i.gross_total_price;
    if (jsonData.invoice_discounts) {
      for (const raw_invoice_discount of jsonData.invoice_discounts) {
        const d = new InvoiceDiscount(
          {
            discount_type: raw_invoice_discount.discount_type
              ? raw_invoice_discount.discount_type
              : InvoiceDiscountType.PERCENT,
            name: raw_invoice_discount.name,
            discount: raw_invoice_discount.discount,
            discount_total: raw_invoice_discount.discount_total,
          },
          newPrice,
        );
        newPrice = newPrice - d.discount_total;
        i.invoice_discounts.push(d);
      }
    }

    i.setInvoiceDiscounts(i.invoice_discounts);

    return i;
  }

  public static createInstances(invoices: any[]): Invoice[] {
    if (!Array.isArray(invoices)) {
      invoices = [invoices];
    }
    return invoices.map(Invoice.createInstance);
  }

  public static createReceivedInvoiceFromPurchaseOrder(po: PurchaseOrder) {
    const i = new Invoice();
    i.invoice_type = "RECEIVED";
    i.gross_total_price = round(po.confirmation_gross_price - po.totalBilled, 2);
    i.credit_note_gross_price = 0;
    i.purchase_order_id = po.purchase_order_id;
    i.round_total_pt = 0.05;
    if (po.supplier) {
      i.supplier = po.supplier;
    }
    if (po.project) {
      i.project = po.project;
      i.project_id = po.project.project_id;
      i.project_corporate_id = po.project.project_corporate_id;
      i.project_name = po.project.name;
    } else if (po.project_id) {
      i.project_id = po.project_id;
    }
    i.currency = po.currency;

    i.setVatRate(po.confirmation_vat);
    return i;
  }
  public static createEmittedInvoice() {
    const i = new Invoice();
    i.invoice_type = "EMITTED";
    return i;
  }

  public getSupplierDiscount(): number {
    return this.purchase_discount_percent ? this.purchase_discount_percent : 0.0;
  }

  public getTotalDiscount(): number {
    return round((this.gross_total_price * this.getSupplierDiscount()) / 100.0, 2) || 0;
  }

  public computeNetTotalPrice(): number {
    return (
      this.gross_total_price - this.purchase_discount_amount - this.credit_note_gross_price - this.totalDiscounts() || 0
    );
  }

  public getNetTotalPrice(): number {
    return this.net_total_price ? this.net_total_price : this.computeNetTotalPrice();
  }

  public getNetTotalPricePc(): number {
    return this.cd_total_bt_pc ? this.cd_total_bt_pc : this.computeNetTotalPrice() * this.cd_fc_rate;
  }

  public getVat(): number {
    return this.vat_amount ? this.vat_amount : this.computeVat();
  }
  public computeVat() {
    return round(this.computeNetTotalPrice() * (this.vat_rate / 100), 2) || 0;
  }
  public getVatPc(): number {
    return this.cd_vat_amount_pc ? this.cd_vat_amount_pc : this.computeVatPc();
  }
  public computeVatPc() {
    return round(this.computeNetTotalPrice() * this.cd_fc_rate * (this.vat_rate / 100), 2) || 0;
  }

  public computeNetVatTotalPrice(): number {
    let total = this.computeNetTotalPrice() + this.computeVat();
    if (this.round_total_pt >= 0.01) {
      return roundToNearestCents(total, this.round_total_pt);
    } else {
      return total;
    }
  }

  public getNetVatTotalPrice(): number {
    return this.total_pt ? this.total_pt : this.computeNetVatTotalPrice();
  }

  public getNetVatTotalPricePc(): number {
    return this.cd_total_pt_pc ? this.cd_total_pt_pc : this.computeNetVatTotalPricePc();
  }

  /**
   * Gets the net VAT total price of the invoice.
   *
   * If status = PAID : paid amount
   * Else : invoice total pt
   */
  public getNetVatRealTotalPrice(): number {
    if (this.status === InvoiceStatus.PAID) {
      return this.paid_total_pt;
    }
    return this.total_pt ? this.total_pt : this.computeNetVatTotalPrice();
  }

  /**
   * Gets the net VAT total price in primary currency of the invoice.
   *
   * If status = PAID : paid amount in pc
   * Else : invoice total pt in pc
   */
  public getNetVatRealTotalPricePc(): number {
    if (this.status === InvoiceStatus.PAID) {
      return this.cd_paid_total_pt_pc;
    }
    return this.cd_total_pt_pc ? this.cd_total_pt_pc : this.computeNetVatTotalPricePc();
  }

  /**
   * Gets the VAT in primary currency of the invoice.
   *
   * If status = PAID : paid VAT amount in pc
   * Else : invoice VAT in pc
   */
  public getRealVatPc(): number {
    if (this.status === InvoiceStatus.PAID) {
      return this.cd_paid_vat_amount_pc;
    }
    return this.cd_vat_amount_pc ? this.cd_vat_amount_pc : 0;
  }

  /**
   * Gets the gross total price in primary currency of the invoice.
   *
   * If status = PAID : paid gross amount in pc
   * Else : invoice gross total price in pc
   */
  public getGrossRealPricePc(): number {
    if (this.status === InvoiceStatus.PAID) {
      return this.cd_paid_gross_total_price_pc;
    }
    return this.cd_gross_total_price_pc;
  }

  /**
   * Gets the net total price (after discounts) in primary currency of the invoice.
   *
   * If status = PAID : paid HT amount in pc (already net since payments cover the discounted amount)
   * Else : invoice net amount in pc (delegates to getNetTotalPricePc)
   */
  public getNetRealPricePc(): number {
    if (this.status === InvoiceStatus.PAID) {
      return this.cd_paid_gross_total_price_pc;
    }
    return this.getNetTotalPricePc();
  }

  public computeNetVatTotalPricePc(): number {
    let total = this.computeNetTotalPrice() * this.cd_fc_rate + this.computeVatPc();
    if (this.round_total_pt >= 0.01) {
      return roundToNearestCents(total, this.round_total_pt);
    } else {
      return total;
    }
  }

  public getRateCurrency(companyCurrency): number {
    if (this.currency_details && this.currency_details.fc_rate && this.currency !== companyCurrency) {
      return this.cd_fc_rate;
    }
    return 1;
  }

  get hasPayments() {
    return this.payments && this.payments.length > 0;
  }

  get paidAmountDiffers(): boolean {
    return Math.abs(this.netVatTotalPrice - this.paid_total_pt) > 1;
  }

  get leftToPayTtc() {
    if (this.status === InvoiceStatus.CANCELLED || this.status === InvoiceStatus.PAID) {
      return 0;
    }
    return this.paid_total_pt > this.getNetVatTotalPrice() ? 0 : this.getNetVatTotalPrice() - this.paid_total_pt;
  }

  get leftToPayTtcPc() {
    return this.leftToPayTtc * (this.cd_fc_rate == 0 ? 1 : this.cd_fc_rate);
  }

  get leftToPay(): number {
    return this.paid_gross_total_price > this.gross_total_price
      ? 0
      : this.gross_total_price - this.paid_gross_total_price;
  }

  get leftToPayIncludingDiscounts(): number {
    if (this.status === InvoiceStatus.CANCELLED || this.status === InvoiceStatus.PAID) {
      return 0;
    }
    let net = this.getNetTotalPrice();
    return this.paid_gross_total_price > net ? 0 : net - this.paid_gross_total_price;
  }

  get leftToPayIncludingDiscountsPc(): number {
    if (this.status === InvoiceStatus.CANCELLED || this.status === InvoiceStatus.PAID) {
      return 0;
    }
    let net = this.getNetTotalPricePc();
    return this.cd_paid_gross_total_price_pc > net ? 0 : net - this.cd_paid_gross_total_price_pc;
  }

  public recalculatePrices() {
    this.net_total_price = this.computeNetTotalPrice();
    this.vat_amount = this.computeVat();
    this.total_pt = this.computeNetVatTotalPrice();
  }

  clone(removePO = false): Invoice {
    let i = Object.assign(new Invoice(), this);
    if (removePO) {
      i.purchase_order = null;
    }
    return i;
  }

  getFormErrors(purchaseOrder?: PurchaseOrder, creditNote?: CreditNote): string[] {
    const errors = [];

    if (this.projects_repartition.length > 0) {
      const repartition_total_gross_amount = this.projects_repartition.reduce((sum, r) => sum + r.gross_amount, 0);
      const EPSILON = 0.05;

      const amountToCompare: number = this.gross_total_price;

      if (
        repartition_total_gross_amount < amountToCompare - EPSILON ||
        repartition_total_gross_amount > amountToCompare + EPSILON
      ) {
        errors.push("Le total des montants par chantier ne correspond pas au montant HT de la facture.");
      }
    }

    if (!this.invoice_corporate_id || this.invoice_corporate_id.trim() == "")
      errors.push("Le numéro de la facture doit être renseigné");

    if (this.getNetVatTotalPrice() < 0) errors.push("Le montant TTC de la facture ne peut pas être négatif.");
    if (!this.payment_deadline) errors.push("La date d'échéance doit être renseignée.");
    if (!this.invoice_asset_id || this.invoice_asset_id.trim() == "")
      errors.push("Une pièce jointe pour la facture est obligatoire.");

    if (purchaseOrder) {
      if (this.invoice_date.getTime() + 24 * 60 * 60 * 1000 < purchaseOrder.order_date.getTime()) {
        errors.push(
          "Vous devez renseigner une date de facture supérieure ou égale à la date de commande (" +
            purchaseOrder.order_date.toLocaleDateString() +
            ")",
        );
      }
    }

    if (creditNote) {
      if (this.credit_note_gross_price) {
        if (this.credit_note_gross_price > creditNote.remaining_amount)
          errors.push(
            "Le montant de la note de crédit doit être inférieur ou égal au montant disponible de la note de crédit.",
          );
      }
    }

    if (this.payment_iban || this.payment_ref || this.payment_add_info) {
      if (this.payment_iban) {
        this.payment_iban = this.payment_iban.replace(/\s/g, "").toUpperCase();

        if (this.isIbanQr() && !this.isRefValidForQr()) {
          errors.push("Un QR-IBAN nécessite la référence QR du bulletin (27 chiffres).");
        }
      }
      if (this.payment_ref) {
        this.payment_ref = this.payment_ref.toUpperCase().replace(/\s/g, "");
      }
      if (this.payment_add_info) {
        this.payment_add_info = this.payment_add_info.replace(/[°@#$%^&*{}\[\]|<>]/g, "");
      }
      if (!this.isIbanValid())
        errors.push(
          this.payment_iban
            ? "L'IBAN doit contenir " +
                IBAN_CODE_LENGTHS[this.payment_iban.substr(0, 2)] +
                " caractères alphanumériques."
            : "L'IBAN est requis.",
        );

      if (this.payment_ref) {
        // Même verdict que le back, qui refuse de générer le fichier de paiement sinon.
        const referenceError = getPaymentReferenceError(this.payment_ref);
        if (referenceError) {
          errors.push(referenceError);
        }
      } else if (!this.payment_add_info || this.payment_add_info.length === 0) {
        errors.push("Le numéro de référence ou l'information additionnelle doit être renseigné.");
      }
    }

    return errors;
  }

  isIbanValid(): boolean {
    return (
      this.payment_iban &&
      this.payment_iban.length === IBAN_CODE_LENGTHS[this.payment_iban.substr(0, 2)] &&
      /^[A-Z0-9]+$/i.test(this.payment_iban) &&
      isValidIbanChecksum(this.payment_iban)
    );
  }

  isIbanQr(): boolean {
    return isQrIban(this.payment_iban);
  }

  isRefValid(): boolean {
    return !getPaymentReferenceError(this.payment_ref);
  }

  /** Un QR-IBAN n'accepte qu'une référence QR : ni une référence RF ni une remise libre. */
  isRefValidForQr(): boolean {
    return classifyPaymentReference(this.payment_ref).kind === ReferenceKind.QRR;
  }

  areRefAndAdditionnalInfoValid(): boolean {
    if (this.payment_ref) {
      return this.isRefValid();
    } else {
      return this.payment_add_info && this.payment_add_info.length > 0;
    }
  }

  public totalDiscounts(isPc = false) {
    if (!this.invoice_discounts) return 0;

    let sellingPrice = isPc ? this.cd_gross_total_price_pc : this.gross_total_price;
    let newPrice = sellingPrice;
    for (const raw_invoice_discount of this.invoice_discounts) {
      let discount = isPc ? raw_invoice_discount.discount * this.cd_fc_rate : raw_invoice_discount.discount;
      const d = new InvoiceDiscount(
        {
          discount_type: raw_invoice_discount.discount_type
            ? raw_invoice_discount.discount_type
            : InvoiceDiscountType.PERCENT,
          name: raw_invoice_discount.name,
          discount: discount,
          discount_total: raw_invoice_discount.discount_total,
        },
        newPrice,
      );
      newPrice -= d.discount_total;
    }
    return sellingPrice - newPrice;
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
